/**
 * The real brand mark, rebuilt as vector from the supplied logo sheet: a navy ring enclosing a
 * geometric gold "A", with a parallel accent stroke running down its left side.
 *
 * Drawn as strokes on a 100-unit square with round caps, matching the artwork's construction, so it
 * stays crisp at the 22px the brand bar uses and at any size above it. The two colours are `currentColor`-
 * free on purpose -- they are the brand's, not the theme's, and the mark reads correctly on both the
 * light and dark surfaces without recolouring.
 *
 * Replaces a placeholder invented for the prototype (a "blade arc under a scoring dot") that had
 * nothing to do with the actual identity.
 */
export function Logo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true">
      <circle cx="50" cy="50" r="40" stroke="#40576a" strokeWidth="6.5" />
      <g stroke="#c29a5a" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round">
        {/* The A: apex, two legs, and the crossbar. The right leg deliberately runs past the ring, as
            it does in the artwork. */}
        <path d="M53.5 21 L31.5 82" />
        <path d="M53.5 21 L82 70" />
        <path d="M42 55.5 L69 55.5" />
        {/* The accent stroke, parallel to the left leg and set just outside it. */}
        <path d="M44 21.5 L21 76" />
      </g>
    </svg>
  );
}

/**
 * The brand bar, in two sizes.
 *
 * `large` is for the signed-out pages and the login screen: a child arriving needs to see at a glance
 * that they are in the right place, and a 22px mark next to 15px text does not do that. Inside the app
 * the small one is right -- by then they know where they are, and the chrome should get out of the way.
 */
export function BrandBar({ size = "small" }: { size?: "small" | "large" }) {
  const large = size === "large";
  return (
    <div className={large ? "brand-bar brand-bar-large" : "brand-bar"}>
      <div className="brand-mark">
        <Logo size={large ? 56 : 22} />
        <span className="brand-name">ATHLETE&rsquo;S ADVANTAGE</span>
      </div>
    </div>
  );
}
